import Environment from '@eatzy/common-reactjs/Environment';
import { api } from './Api';
import { snakeToCamel } from '../utils/CaseUtils';

const options = { cache: 'no-store', headers: { Accept: 'application/json' } };
const positive = (n) => Number.isSafeInteger(n) && n > 0;
const cents = (n) => Number.isSafeInteger(n) && n >= 0;
const version = (v) => typeof v === 'string' && v.trim().length > 0;
const validBody = (b) => b && Object.keys(b).length === 6 &&
  ['amount_minor', 'currency', 'reason', 'eatzy_cost_confirmed', 'policy_version', 'order_version']
    .every((key) => Object.prototype.hasOwnProperty.call(b, key)) &&
  positive(b.amount_minor) && b.amount_minor <= 500 && b.currency === 'USD' &&
  typeof b.reason === 'string' && b.reason.trim().length > 0 && b.reason.length <= 500 &&
  b.eatzy_cost_confirmed === true && version(b.policy_version) && version(b.order_version);

export const getTipPolicy = (orderId) =>
  api.get(`/deliveryops/orders/${orderId}/tip-adjustment-policy`, undefined, options);
export const getTipLedger = async (orderId, page = 1) => {
  const response = await api.get('/deliveryops/orders/tip-adjustments', {
    order_id: orderId, page, page_size: 20,
  }, options);
  if (!Array.isArray(response?.results) || !cents(response.total_results) ||
      !cents(response.total_pages) || response.page !== page) {
    throw new Error('Ledger response unavailable. Retry or contact support.');
  }
  // No guessed totals schema, client-side aggregation, or earnings adjustments.
  return response;
};
export const reconcileTip = (orderId, key) =>
  api.get(`/deliveryops/orders/${orderId}/tip-adjustments/by-request/${encodeURIComponent(key)}`,
    undefined, options);

// Operational map alert only; never use this reporting rule for funding eligibility.
export const mapNoTip = (order = {}) => {
  const summary = snakeToCamel(order.tipSummary || order.tip_summary || {});
  return (order.orderType || order.order_type) === 'delivery' &&
    summary.currency === 'USD' && summary.minorUnitExponent === 2 &&
    summary.driverFeeAvailability === 'known' &&
    summary.eatzyFundedAvailability === 'known' &&
    cents(summary.driverFeeMinor) && cents(summary.eatzyFundedMinor) &&
    summary.driverFeeMinor + summary.eatzyFundedMinor < 100;
};

export const tipDisplay = (order = {}) => {
  const summary = snakeToCamel(order.tipSummary || order.tip_summary || {});
  const type = order.orderType || order.order_type;
  const applicable = !['pickup', 'eat_in'].includes(type) &&
    summary.availability !== 'not_applicable';
  const currencyKnown = summary.currency === 'USD' && summary.minorUnitExponent === 2;
  const known = applicable && currencyKnown && summary.availability === 'known' &&
    cents(summary.customerGratuityMinor);
  return {
    applicable,
    customer: known ? summary.customerGratuityMinor : null,
    funded: applicable && currencyKnown && cents(summary.eatzyFundedMinor)
      ? summary.eatzyFundedMinor : null,
    low: known && summary.customerGratuityMinor < 100 &&
      summary.isLowCustomerGratuity === true,
    reason: summary.reasonCode,
  };
};
export const formatTipAmount = (amount) =>
  cents(amount) ? `$${(amount / 100).toFixed(2)} USD` : 'Unavailable';

export const policyAllowsFunding = (p, orderId, now = Date.now()) =>
  p?.eligible === true && Array.isArray(p.ineligibility_reasons) &&
  p.ineligibility_reasons.length === 0 && p.actor_capability?.can_fund === true &&
  positive(p.business_id) && Array.isArray(p.actor_capability.business_ids) &&
  p.actor_capability.business_ids.includes(p.business_id) &&
  p.order_id === Number(orderId) && Number.isSafeInteger(p.order_status_id) &&
  p.order_status_id >= 0 && p.order_status_id < 8 && positive(p.driver_id) &&
  p.tip_summary?.availability === 'known' && p.tip_summary.currency === 'USD' &&
  p.tip_summary.minor_unit_exponent === 2 &&
  cents(p.tip_summary.customer_gratuity_minor) &&
  p.tip_summary.customer_gratuity_minor < 100 &&
  p.tip_summary.is_low_customer_gratuity === true &&
  p.tip_summary.low_gratuity_threshold_minor === 100 &&
  p.tip_summary.eatzy_funded_minor === 0 &&
  p.minimum_amount_minor === 1 && p.increment_minor === 1 &&
  positive(p.maximum_amount_minor) && p.maximum_amount_minor <= 500 &&
  p.already_funded_minor === 0 && positive(p.remaining_allowance_minor) &&
  p.remaining_allowance_minor <= p.maximum_amount_minor && p.repeat_allowed === false &&
  version(p.policy_version) && version(p.order_version) &&
  typeof p.expires_at_utc === 'string' && /Z$/.test(p.expires_at_utc) &&
  Date.parse(p.expires_at_utc) > now;

export const makeTipBody = (policy, orderId, amount, reason, confirmed) => {
  if (!policyAllowsFunding(policy, orderId)) throw new Error('Funding policy unavailable, expired or ineligible. Reload and review.');
  if (!positive(amount) || amount > 500 || amount > policy.remaining_allowance_minor) {
    throw new Error('Enter a whole-cent amount from 1 to the available limit (at most 500).');
  }
  if (typeof reason !== 'string' || !reason.trim() || reason.trim().length > 500) {
    throw new Error('Enter a reason of 1–500 characters.');
  }
  if (confirmed !== true) throw new Error('Confirm explicitly that Eatzy pays this amount.');
  return {
    amount_minor: amount, currency: 'USD', reason: reason.trim(),
    eatzy_cost_confirmed: true, policy_version: policy.policy_version,
    order_version: policy.order_version,
  };
};

// Only account ID, order ID, random request key and exact financial request are
// stored. Never store tokens, credentials, role assignments or customer details.
const storageKey = (actor, order) => {
  if (!positive(Number(actor)) || !positive(Number(order))) {
    throw new Error('Authenticated account and native order ID required.');
  }
  return `eatzy-funded-tip:v1:${encodeURIComponent(Environment.getEnv('API_URL'))}:${actor}:${order}`;
};
const listeners = new Set();
export const subscribeTipRequests = (listener) => {
  listeners.add(listener);
  const storageListener = () => listener();
  window.addEventListener('storage', storageListener);
  return () => { listeners.delete(listener); window.removeEventListener('storage', storageListener); };
};
const notify = () => listeners.forEach((listener) => listener());
export const readTipRequest = (actor, order) => {
  const raw = window.localStorage.getItem(storageKey(actor, order));
  if (!raw) return null;
  let record;
  try { record = JSON.parse(raw); } catch { throw new Error('Stored funding request is unreadable. Stop and contact support.'); }
  const b = record?.body;
  if (record.actor !== String(actor) || record.order !== String(order) ||
      !version(record.key) || record.key.length > 128 ||
      !validBody(b) ||
      !['uncertain', 'accepted', 'blocked'].includes(record.state) ||
      (record.state === 'accepted' && !acceptedAdjustment(record.response, record))) {
    throw new Error('Stored funding request is invalid. Stop and contact support.');
  }
  return record;
};
const save = (actor, order, record) => {
  const key = storageKey(actor, order);
  const current = readTipRequest(actor, order);
  if (current && current.key !== record.key) {
    throw new Error('A different retained request exists. Stop and contact support.');
  }
  if (current?.state === 'accepted' && record.state !== 'accepted') return current;
  window.localStorage.setItem(key, JSON.stringify(record));
  if (window.localStorage.getItem(key) !== JSON.stringify(record)) {
    throw new Error('Unable to retain the request safely. No new submission is allowed.');
  }
  notify();
  return record;
};
const newRequestKey = () => {
  if (!window.crypto?.getRandomValues) throw new Error('Secure request key generation unavailable.');
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};
export const retainTipRequest = (actor, order, body) => {
  if (!validBody(body)) throw new Error('Invalid funding request. Review amount, reason, confirmation and versions.');
  if (readTipRequest(actor, order)) throw new Error('Resolve the retained request first. Do not create another tip.');
  return save(actor, order, {
    actor: String(actor), order: String(order), key: newRequestKey(),
    body: JSON.parse(JSON.stringify(body)), state: 'uncertain',
  });
};

const acceptedAdjustment = (response, request) => {
  const a = response?.adjustment;
  return positive(a?.id) && a.order_id === Number(request.order) &&
    a.amount_minor === request.body.amount_minor && a.currency === 'USD' &&
    ['pending', 'applied'].includes(a.status);
};
export const fundedTipErrorCode = (error) =>
  [error?.code, error?.error_code, error?.reason_code, error?.error?.code, error?.message]
    .find((value) => typeof value === 'string' && /^[a-z_]+$/.test(value)) || 'outcome_unknown';
const definiteRejections = new Set([
  'stale_policy_or_order', 'amount_out_of_range', 'unsupported_currency',
  'reason_required', 'eatzy_cost_confirmation_required',
  'status_cutoff_reached', 'driver_unassigned', 'driver_assignment_ambiguous',
  'gratuity_source_missing', 'fee_descriptor_missing', 'fee_amount_missing',
  'service_fee_type_missing', 'negative_gratuity_fee', 'gratuity_not_minor_unit',
  'customer_gratuity_at_or_above_threshold', 'gratuity_aggregate_indeterminate',
  'gratuity_amount_indeterminate', 'gratuity_source_unavailable',
  'funding_summary_unavailable', 'payout_already_started', 'compensation_already_recorded',
]);
const inflight = new Map();
const withOutcomeTimeout = async (operation) => {
  let timer;
  try {
    return await Promise.race([
      operation,
      new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(new Error('outcome_unknown')), 30000);
      }),
    ]);
  } finally { clearTimeout(timer); }
};
export const resolveTipRequest = async (actor, order, action = 'reconcile', expectedRequest) => {
  const scope = storageKey(actor, order);
  const request = readTipRequest(actor, order);
  if (!request) throw new Error('No retained request to reconcile.');
  if (expectedRequest && (request.key !== expectedRequest.key ||
      JSON.stringify(request.body) !== JSON.stringify(expectedRequest.body))) {
    throw new Error('The retained request changed in another view. Stop and review it before proceeding.');
  }
  // A reconciliation read must be possible while an earlier POST is in flight.
  const flightKey = `${scope}:${action}`;
  if (inflight.has(flightKey)) return inflight.get(flightKey);
  if (request.state === 'accepted') return { accepted: true, response: request.response };
  if (action === 'retry' && request.state === 'blocked') {
    throw new Error('Submission blocked. Reconcile or contact support; do not retry.');
  }
  const operation = (async () => {
    try {
      const response = await withOutcomeTimeout(action === 'retry'
        ? api.post(`/deliveryops/orders/${order}/tip-adjustments`, request.body, {
          ...options, headers: { ...options.headers, 'Idempotency-Key': request.key },
        })
        : reconcileTip(order, request.key));
      if (acceptedAdjustment(response, request)) {
        // A storage failure after acceptance must not erase the retained request.
        const a = response.adjustment;
        const storedResponse = {
          adjustment: { id: a.id, order_id: a.order_id, amount_minor: a.amount_minor,
            currency: a.currency, status: a.status },
        };
        // Store only documented recovery evidence, never a wholesale response
        // that could acquire sensitive fields in a later API release.
        try { save(actor, order, { ...request, state: 'accepted', response: storedResponse }); }
        catch {
          return { accepted: true, response,
            message: 'Funding is recorded, but recovery storage could not be updated. Keep the retained request and check the ledger before any further action.' };
        }
        return { accepted: true, response };
      }
      return { accepted: false, message: 'Outcome unresolved. Keep this request; check again or retry the identical submission. If it remains unclear, contact Eatzy support.' };
    } catch (error) {
      const current = readTipRequest(actor, order);
      if (current?.key === request.key && current.state === 'accepted') {
        return { accepted: true, response: current.response };
      }
      const code = fundedTipErrorCode(error);
      if (action === 'retry' && [409, 422].includes(error?.status) && definiteRejections.has(code)) {
        window.localStorage.removeItem(scope);
        notify();
        return { accepted: false, rejected: true, message: `${code}: reload policy, review and confirm again.` };
      }
      if (['funding_capability_required', 'business_scope_forbidden',
        'idempotency_mismatch', 'adjustment_already_exists'].includes(code) ||
        [401, 403].includes(error?.status)) {
        save(actor, order, { ...request, state: 'blocked' });
        return { accepted: false, message: `${code}: stop and reconcile. If your session expired, sign in again as the same dispatcher. Review the ledger or contact support. Do not change account or request key to retry.` };
      }
      return { accepted: false, message: 'Outcome unknown. The exact request is retained. Reconcile before any further action; never submit a second request.' };
    }
  })();
  inflight.set(flightKey, operation);
  try { return await operation; } finally { inflight.delete(flightKey); }
};

export const refreshTipQueries = (cache) => [
  'tip-policy', 'tip-ledger', 'active-orders', 'active-drivers', 'driver-details',
  'delivery-item', 'order-history', 'order-search', 'order-receipt', 'customer-detail',
  'drivers-history-v2',
].forEach((key) => cache.invalidateQueries(key));
